import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, ShieldCheck, Trash2, X } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import {
  useAtualizarTemplateClausula,
  useCriarTemplateClausula,
  useDesativarTemplateClausula,
  useTemplatesClausulasAdmin,
} from '../hooks';
import type { ClauseTemplate, ClauseTemplatePayload } from '../api';

function TemplateForm({
  initial,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  initial?: ClauseTemplate;
  onSubmit: (payload: ClauseTemplatePayload) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [modality, setModality] = useState(initial?.modality ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [template, setTemplate] = useState(initial?.template ?? '');
  const [isMandatory, setIsMandatory] = useState(initial?.isMandatory ?? true);
  const [err, setErr] = useState('');

  const handleSubmit = () => {
    if (!name.trim() || !slug.trim() || !modality.trim()) {
      setErr('Nome, slug e modalidade são obrigatórios.');
      return;
    }
    setErr('');
    onSubmit({
      name,
      slug,
      modality,
      description: description || undefined,
      template: template || undefined,
      isMandatory,
    });
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 mb-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{initial ? 'Editar cláusula' : 'Nova cláusula'}</h3>
        <button onClick={onCancel} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="grid grid-cols-2 gap-3">
        <TextInput label="Nome *" value={name} onChange={(e) => setName(e.target.value)} />
        <TextInput label="Slug * (ex: confidencialidade)" value={slug} onChange={(e) => setSlug(e.target.value)} disabled={!!initial} />
      </div>
      <TextInput label="Modalidade * (ex: servicos, fornecimento)" value={modality} onChange={(e) => setModality(e.target.value)} />
      <TextInput label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} />
      <Textarea label="Texto da cláusula" value={template} onChange={(e) => setTemplate(e.target.value)} rows={6} />
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} />
        Obrigatória nesta modalidade (bloqueia a conclusão do contrato se ausente)
      </label>
      <div className="flex gap-2">
        <Button loading={isSubmitting} onClick={handleSubmit}>Salvar</Button>
        <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

/** Administração do catálogo de cláusulas obrigatórias por modalidade (RN-COM-029). */
export default function ContratosClausulasPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can(FEATURES.contratosClausulas, 'create');
  const canEdit = can(FEATURES.contratosClausulas, 'edit');
  const canDelete = can(FEATURES.contratosClausulas, 'delete');

  const { data: templates = [], isLoading } = useTemplatesClausulasAdmin();
  const { mutate: criar, isPending: isCreating } = useCriarTemplateClausula();
  const { mutate: atualizar, isPending: isUpdating } = useAtualizarTemplateClausula();
  const { mutate: desativar } = useDesativarTemplateClausula();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ClauseTemplate | null>(null);

  const grouped = templates.reduce<Record<string, ClauseTemplate[]>>((acc, t) => {
    (acc[t.modality] ??= []).push(t);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/contratos')} aria-label="Voltar" className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold text-gray-900">Cláusulas Obrigatórias por Modalidade</h1>
      </div>

      {canCreate && !showForm && (
        <Button size="sm" variant="secondary" icon={<Plus className="w-3.5 h-3.5" />} onClick={() => { setEditing(null); setShowForm(true); }}>
          Nova Cláusula
        </Button>
      )}

      {showForm && (
        <TemplateForm
          initial={editing ?? undefined}
          isSubmitting={isCreating || isUpdating}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={(payload) => {
            if (editing) {
              atualizar({ id: editing.id, payload }, { onSuccess: () => { setShowForm(false); setEditing(null); } });
            } else {
              criar(payload, { onSuccess: () => setShowForm(false) });
            }
          }}
        />
      )}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : templates.length === 0 ? (
        <EmptyState title="Nenhuma cláusula cadastrada ainda" />
      ) : (
        Object.entries(grouped).map(([modality, items]) => (
          <div key={modality} className="bg-white rounded-lg border border-gray-200">
            <h2 className="px-4 py-3 text-sm font-semibold text-gray-900 border-b border-gray-100">{modality}</h2>
            <ul className="divide-y divide-gray-100">
              {items.map((t) => (
                <li key={t.id} className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                      {t.name}
                      {t.isMandatory && (
                        <span title="Obrigatória">
                          <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                        </span>
                      )}
                      {!t.isActive && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inativa</span>}
                    </p>
                    {t.description && <p className="text-xs text-gray-500">{t.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canEdit && (
                      <button onClick={() => { setEditing(t); setShowForm(true); }} aria-label="Editar" className="text-gray-400 hover:text-blue-600">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && t.isActive && (
                      <button onClick={() => desativar(t.id)} aria-label="Desativar" className="text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}
